/**
 * «Clientes» in «Análisis», besides who owes and who stopped coming (the owner, 2026-10-13, of what
 * the page could add: «let's build all of it»): the regulars whose day to buy has come, each by
 * their own rhythm; and how the fiado goes: what was given on credit and collected in the period,
 * what selling on credit left once what was given up on is taken off, and who owes more than a
 * month ago, against their limit, and how long they take to pay.
 *
 * Sales are counted as everywhere in «Análisis» (`netoDeVenta`). A client's account is their
 * notebook (`estadoDeCuenta`), paid off oldest first. Pure, so the screen and its tests share one
 * answer.
 */

import { diasEntre, sumarDias, type Centavos } from '@abastece/shared';
import { fiadoDeVenta, netoDeVenta, type Abono, type Cliente, type Devolucion, type Incobrable, type Venta } from '@abastece/core';
import type { Tramo } from './analisis.js';
import { estadoDeCuenta, type Movimiento } from './fiado.js';
import { pasoSuLimite } from './porCobrar.js';

/** How far back a client's rhythm, and how they pay, are read: half a year. */
export const DIAS_DE_CLIENTE = 180;
/** The days a client must have bought on for a rhythm: three gaps between them. */
export const VECES_PARA_RITMO = 4;
/** Past this many days without buying, a regular is in «Dejaron de venir», not here. */
export const DIAS_IDO = 30;
/** What a client owes is compared with what they owed this many days before. */
export const DIAS_ANTES = 30;

// ---------------------------------------------------------------------------
// Les toca comprar
// ---------------------------------------------------------------------------

export interface LeToca {
  clienteId: string;
  /** Their usual days from one day they buy to the next: the middle of their gaps. */
  cadencia: number;
  /** The last day they bought, and the days since. */
  ultima: string;
  desde: number;
  /** What they spend on a day they buy, on average. */
  porVez: Centavos;
}

const mediana = (xs: readonly number[]): number => {
  const o = [...xs].sort((a, b) => a - b);
  const m = Math.floor(o.length / 2);
  return o.length % 2 ? o[m]! : Math.round((o[m - 1]! + o[m]!) / 2);
};

/**
 * The regulars whose day has come: clients who bought on four days or more the last half year,
 * and as many days have gone by since the last as usually go between one and the next, or more.
 * Their rhythm is the middle of their gaps, so one long absence does not stretch it. Who has been
 * gone a month is in «Dejaron de venir». The latest for their rhythm first, then who spends most.
 */
export function lesTocaComprar(ventas: readonly Venta[], hoy: string): LeToca[] {
  const desde = sumarDias(hoy, -(DIAS_DE_CLIENTE - 1));
  const porCliente = new Map<string, { dias: Set<string>; ingreso: number }>();
  for (const v of ventas) {
    if (v.anulada || !v.clienteId || v.fecha < desde || v.fecha > hoy) continue;
    const x = porCliente.get(v.clienteId) ?? { dias: new Set<string>(), ingreso: 0 };
    x.dias.add(v.fecha);
    x.ingreso += netoDeVenta(v).ingreso;
    porCliente.set(v.clienteId, x);
  }
  const lista: LeToca[] = [];
  for (const [clienteId, x] of porCliente) {
    if (x.dias.size < VECES_PARA_RITMO) continue;
    const dias = [...x.dias].sort();
    const huecos = dias.slice(1).map((d, i) => diasEntre(dias[i]!, d));
    const cadencia = mediana(huecos);
    const ultima = dias.at(-1)!;
    const pasaron = diasEntre(ultima, hoy);
    if (pasaron < cadencia || pasaron >= DIAS_IDO) continue;
    lista.push({ clienteId, cadencia, ultima, desde: pasaron, porVez: Math.round(x.ingreso / dias.length) as Centavos });
  }
  return lista.sort((a, b) => b.desde / b.cadencia - a.desde / a.cadencia || b.porVez - a.porVez);
}

// ---------------------------------------------------------------------------
// Cómo va el fiado
// ---------------------------------------------------------------------------

export interface FiadoDelPeriodo {
  /** What was sold on credit: the part of each sale paid «Fiado». */
  dado: Centavos;
  /** What clients paid on their accounts. */
  cobrado: Centavos;
  /** What the owner gave up on. */
  perdido: Centavos;
  /** What the sales on credit left: each sale's profit in the part of it given on credit. */
  utilidad: Centavos;
  /** That, less what was given up on. */
  queda: Centavos;
}

/**
 * The period's fiado: given, collected, given up on, and whether selling on credit paid: what the
 * credit sales left, each in the part of it on credit, less what was written off in the period.
 */
export function fiadoDelPeriodo(d: {
  ventas: readonly Venta[];
  abonos: readonly Abono[];
  incobrables: readonly Incobrable[];
  tramo: Tramo;
}): FiadoDelPeriodo {
  const dentro = (fecha: string) => fecha >= d.tramo.desde && fecha <= d.tramo.hasta;
  let dado = 0;
  let utilidad = 0;
  for (const v of d.ventas) {
    if (v.anulada || !dentro(v.fecha)) continue;
    const fiado = fiadoDeVenta(v);
    if (fiado <= 0 || v.total <= 0) continue;
    dado += fiado;
    const neto = netoDeVenta(v);
    utilidad += ((neto.ingreso - neto.costo) * fiado) / v.total;
  }
  const cobrado = d.abonos.reduce((a, x) => (!x.anulado && dentro(x.fecha) ? a + x.monto : a), 0);
  const perdido = d.incobrables.reduce((a, x) => (!x.anulado && dentro(x.fecha) ? a + x.monto : a), 0);
  return {
    dado: dado as Centavos,
    cobrado: cobrado as Centavos,
    perdido: perdido as Centavos,
    utilidad: Math.round(utilidad) as Centavos,
    queda: (Math.round(utilidad) - perdido) as Centavos,
  };
}

export interface RiesgoDeFiado {
  clienteId: string;
  nombre: string;
  debe: Centavos;
  /** What they owed `DIAS_ANTES` days ago. */
  antes: Centavos;
  limite: Centavos | null;
  /** What they owe of their limit, in basis points; null with none. */
  usoBp: number | null;
  /**
   * How many days a fiado of theirs took to be paid off, on average by amount, of those an abono
   * finished paying the last half year; null when none was.
   */
  diasParaPagar: number | null;
}

/**
 * Each client who owes, read from their notebook: against what they owed a month ago, against
 * their limit, and how long their fiado takes to be paid, abonos paying the oldest first as on
 * their own page. Past their limit first, then whoever owes more than a month ago by most, then
 * whoever owes most.
 */
export function riesgoDelFiado(d: {
  deudores: readonly Pick<Cliente, 'id' | 'nombre' | 'saldoInicial' | 'saldoInicialFecha' | 'limiteCredito'>[];
  ventas: readonly Venta[];
  abonos: readonly Abono[];
  devoluciones: readonly Devolucion[];
  incobrables: readonly Incobrable[];
  hoy: string;
}): RiesgoDeFiado[] {
  const deCada = <T extends { clienteId: string | null }>(filas: readonly T[]): Map<string, T[]> => {
    const m = new Map<string, T[]>();
    for (const f of filas) if (f.clienteId) m.set(f.clienteId, [...(m.get(f.clienteId) ?? []), f]);
    return m;
  };
  const ventas = deCada(d.ventas);
  const abonos = deCada(d.abonos);
  const devoluciones = deCada(d.devoluciones);
  const incobrables = deCada(d.incobrables);
  const corte = sumarDias(d.hoy, -DIAS_ANTES);
  const lista: RiesgoDeFiado[] = [];
  for (const c of d.deudores) {
    const movimientos = estadoDeCuenta(ventas.get(c.id) ?? [], abonos.get(c.id) ?? [], devoluciones.get(c.id) ?? [], c, incobrables.get(c.id) ?? []);
    const debe = movimientos.at(-1)?.saldo ?? 0;
    if (debe <= 0) continue;
    const antes = movimientos.filter((m) => m.fecha <= corte).at(-1)?.saldo ?? 0;
    const limite = c.limiteCredito && c.limiteCredito > 0 ? c.limiteCredito : null;
    lista.push({
      clienteId: c.id,
      nombre: c.nombre,
      debe: debe as Centavos,
      antes: Math.max(0, antes) as Centavos,
      limite,
      usoBp: limite ? Math.round((debe * 10_000) / limite) : null,
      diasParaPagar: diasParaPagar(movimientos, sumarDias(d.hoy, -(DIAS_DE_CLIENTE - 1))),
    });
  }
  // Past it as «Fiado» says it: owing more than the limit, not its rounded share.
  const pasado = (r: RiesgoDeFiado) => (pasoSuLimite({ saldo: r.debe, limite: r.limite }) ? 1 : 0);
  return lista.sort((a, b) => pasado(b) - pasado(a) || b.debe - b.antes - (a.debe - a.antes) || b.debe - a.debe);
}

/**
 * The days each fiado took to be paid off, by amount, of those an abono finished from `desde` on:
 * everything that takes from the account pays the oldest first, as `pendientesDelFiado` does, but
 * only an abono paying the last of one says how long the client takes. A return or a write-off is
 * not the client paying.
 */
export function diasParaPagar(movimientos: readonly Movimiento[], desde: string): number | null {
  const pendientes: { fecha: string; monto: number; resta: number }[] = [];
  let aFavor = 0;
  let pesados = 0;
  let total = 0;
  for (const m of movimientos) {
    if (m.monto > 0) {
      const cubierto = Math.min(aFavor, m.monto);
      aFavor -= cubierto;
      if (m.monto - cubierto > 0) pendientes.push({ fecha: m.fecha, monto: m.monto, resta: m.monto - cubierto });
      continue;
    }
    let pago = -m.monto;
    while (pago > 0 && pendientes.length > 0) {
      const primero = pendientes[0]!;
      const toma = Math.min(pago, primero.resta);
      primero.resta -= toma;
      pago -= toma;
      if (primero.resta > 0) continue;
      pendientes.shift();
      if (m.tipo === 'abono' && m.fecha >= desde) {
        pesados += Math.max(0, diasEntre(primero.fecha, m.fecha)) * primero.monto;
        total += primero.monto;
      }
    }
    aFavor += pago;
  }
  return total > 0 ? Math.round(pesados / total) : null;
}
